import React, { useState, useEffect } from "react";
import { Box } from "src/components/Box";
import { Input } from "src/components/Input";
import { TextArea } from "src/components/TextArea";
import { Dropdown } from "src/components/Dropdown";
import { FilePickerWithPreview } from "src/components/FilePicker";
import { Text } from "src/components/Text";

import { Button } from "src/components/Button";
import { useTranslation } from "src/hooks/useTranslation";
import { LegacyOwnerInfoModal } from "./LegacyOwnerInfo.modal";
import { Link, useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  useOwnerInfoState,
  IPassportCitizenship,
} from "./LegacyOwnerInfo.state";
import { PASSPORT_ROUTES } from "src/constants/routes";
import { LoadingOverlay } from "src/components/LoadingOverlay";
import { usePassportStore } from "src/stores/Passport.store";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { CookieNames, setCookie } from "src/helpers/cookies";
import { useErrorModalStore } from "src/components/ErrorModal/ErrorModal.store";
import { Logger } from "src/helpers/logger";
import { errFormatter } from "src/helpers/errorFormatter";
import { Info } from "@asphalt-react/iconpack";
import { AlertBox } from "src/components/AlertBox";
import { Breakpoint } from "src/components/Breakpoint";
import {
  BoxButtonCtaEl,
  BoxColumnEl,
  BoxColumnSectionEl,
  BoxDropdownEl,
} from "./LegacyOwnerInfo.styled";
import { usePassportImprovements } from "../../../../hooks";

export const LegacyOwnerInfoForm = observer(() => {
  const translate = useTranslation();
  const passportStore = usePassportStore();
  const ownerInfoState = useOwnerInfoState();
  const {
    form,
    errorFields,
    citizenshipOptions,
    selectedCitizenship,
    isFieldNotValid,
  } = ownerInfoState;
  const {
    selectedBusinessType,
    selectedPaymentMethod,
    outlet,
    authUser,
  } = passportStore!;
  const [showModalKTP, setShowModalKTP] = useState(false);
  const history = useHistory();

  const errorModalStore = useErrorModalStore();
  const {
    isImprovementEnabled,
    enableImprovementsAndRedirect,
  } = usePassportImprovements();

  useEffect(() => {
    eventDispatcher.publish({ action: "ob_owner_opened" });
  }, []);

  const _handleOnSubmit = async (e: any) => {
    try {
      e?.preventDefault();

      if (!isImprovementEnabled) {
        await enableImprovementsAndRedirect();
        return;
      }

      await passportStore?.updateOutlet(ownerInfoState.validForm);
      eventDispatcher.publish({ action: "ob_owner_next" });
      setCookie(CookieNames.PassportStep, PASSPORT_ROUTES.businessInfo);
      history.push(PASSPORT_ROUTES.businessInfo);
    } catch (err) {
      Logger.error(err as Error);
      const { status, message } = errFormatter(err);

      eventDispatcher.publish({
        action: "ob_owner_error",
        payload: {
          message: message,
          httpStatus: status,
        },
      });
      errorModalStore.showAlert();
      errorModalStore.retryCta = _handleOnSubmit;
    }
  };

  const _handleOnUpload = async (file: File, name: string) => {
    try {
      if (name.toLowerCase().includes("ktp")) {
        ownerInfoState.errorFields.ktp_upload.error = false;
        await ownerInfoState.uploadFileKtp(passportStore?.authUser?.id!, file);
        eventDispatcher.publish({ action: "ob_upload_ktp_success" });
      }
    } catch (error) {
      ownerInfoState.errorFields.ktp_upload.error = true;
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_ktp_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
    }

    try {
      if (name.toLowerCase().includes("kitas")) {
        ownerInfoState.errorFields.kitas_upload.error = false;
        await ownerInfoState.uploadFileKitas(
          passportStore?.authUser?.id!,
          file
        );
        eventDispatcher.publish({
          action: "ob_upload_kitas_success",
          payload: {
            fileType: file.type,
          },
        });
      }
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_kitas_error",
        payload: {
          message: message,
          httpStatus: status,
        },
      });
      ownerInfoState.errorFields.kitas_upload.error = true;
    }

    try {
      if (name.toLowerCase().includes("passport")) {
        ownerInfoState.errorFields.passport_upload.error = false;
        await ownerInfoState.uploadFilePassport(
          passportStore?.authUser?.id!,
          file
        );
        eventDispatcher.publish({
          action: "ob_upload_passport_success",
        });
      }
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_passport_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
      ownerInfoState.errorFields.passport_upload.error = true;
    }

    try {
      if (name.toLowerCase().includes("npwp")) {
        ownerInfoState.errorFields.npwp_upload.error = false;
        await ownerInfoState.uploadFileNpwp(passportStore?.authUser.id!, file);
        eventDispatcher.publish({ action: "ob_upload_npwp_success" });
      }
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_npwp_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
      ownerInfoState.errorFields.npwp_upload.error = true;
    }

    ownerInfoState.applyFileUpload(file, name);
  };

  useEffect(() => {
    ownerInfoState.fillForm(outlet, authUser);
  }, [outlet, ownerInfoState, authUser]);

  return (
    <>
      <Breakpoint medium up>
        <Text marginBottomXl fontSize="xlarge" fontWeight="demi">
          {translate("owner_info.title")}
        </Text>
      </Breakpoint>
      <Box as="form" onSubmit={_handleOnSubmit}>
        <Box marginBottomXl>
          <AlertBox color="info" icon={<Info />}>
            {translate(
              `owner_info.${selectedBusinessType}.${selectedCitizenship.toLowerCase()}.alert`
            )}
          </AlertBox>
        </Box>
        <BoxColumnEl>
          <Box>
            <Input
              label={translate(`owner_info.${selectedBusinessType}.name.label`)}
              placeholder={translate(
                `owner_info.${selectedBusinessType}.name.placeholder`
              )}
              onChange={(e) =>
                ownerInfoState.handleOnFieldChange(
                  "director_name",
                  e.target.value
                )
              }
              value={form.director_name}
              onBlur={() => {
                ownerInfoState.validateForm("director_name");
              }}
              error={errorFields.director_name.error}
              helperText={errorFields.director_name.helperText}
              pattern="^[a-z A-Z .]*$"
            />
          </Box>
          <BoxDropdownEl marginBottomL>
            <Dropdown
              items={citizenshipOptions}
              label={translate(
                `owner_info.${selectedBusinessType}.citizenship.label`
              )}
              placeholder={translate(
                `owner_info.${selectedBusinessType}.citizenship.placeholder`
              )}
              onChange={({ id }) => {
                ownerInfoState.applySelectedCitizenship(
                  id as IPassportCitizenship
                );
              }}
              size="m"
            />
          </BoxDropdownEl>
        </BoxColumnEl>

        {selectedCitizenship === "WNI" && (
          <BoxColumnSectionEl>
            <Box marginBottomL>
              <Text marginBottomS fontWeight="demi" fontSize="normal">
                {translate(
                  `owner_info.${selectedBusinessType}.upload_identity_card.label`
                )}
              </Text>
              <FilePickerWithPreview
                onInfoClick={() => {
                  setShowModalKTP(true);
                  eventDispatcher.publish({ action: "ob_upload_ktp_clicked" });
                }}
                onClick={() =>
                  eventDispatcher.publish({ action: "ob_upload_ktp" })
                }
                maxSize={10}
                onChange={(file) => _handleOnUpload(file, "ktp")}
                previewUrl={ownerInfoState.fileUpload?.ktp.url}
                mediaType={ownerInfoState.fileUpload?.ktp.mediaType}
                metadata={ownerInfoState.fileUpload?.ktp.metadata}
                isLoading={ownerInfoState?.loadingState.has.uploadFileKtp}
                tempFile={ownerInfoState.fileUpload.ktp.temp}
                onDelete={() => {
                  ownerInfoState.deleteFileUpload("ktp");
                  ownerInfoState.errorFields.ktp_upload.error = undefined;
                }}
                onError={(error) => {
                  eventDispatcher.publish({
                    action: "ob_upload_ktp_error",
                    payload: { message: error.message },
                  });
                }}
                isError={ownerInfoState.errorFields.ktp_upload.error}
              />
            </Box>

            <Box>
              <Box>
                <Input
                  label={translate(
                    `owner_info.${selectedBusinessType}.name_on_identity_card.label`
                  )}
                  placeholder={translate(
                    `owner_info.${selectedBusinessType}.name_on_identity_card.placeholder`
                  )}
                  onChange={(e) =>
                    ownerInfoState.handleOnFieldChange(
                      "id_name",
                      e.target.value
                    )
                  }
                  onBlur={() => {
                    ownerInfoState.validateForm("id_name");
                  }}
                  error={errorFields.id_name.error}
                  helperText={errorFields.id_name.helperText}
                  value={form.id_name}
                  maxLength={50}
                  pattern="^[a-z A-Z .]*$"
                />
              </Box>
              <Box>
                <Input
                  label={translate(
                    `owner_info.${selectedBusinessType}.identity_number.label`
                  )}
                  placeholder={translate(
                    `owner_info.${selectedBusinessType}.identity_number.placeholder`
                  )}
                  onChange={({ target }) => {
                    let { value } = target;
                    ownerInfoState.handleOnFieldChange("id_number", value);
                  }}
                  onBlur={() => {
                    ownerInfoState.validateForm("id_number", "id_number");
                  }}
                  error={errorFields.id_number.error}
                  helperText={errorFields.id_number.helperText}
                  value={form.id_number}
                  pattern="^[0-9]*$"
                  maxLength={16}
                  inputMode="numeric"
                />
              </Box>
            </Box>
          </BoxColumnSectionEl>
        )}

        {selectedCitizenship === "WNA" && (
          <BoxColumnSectionEl>
            <Box marginBottomL>
              <Text marginBottomS fontWeight="demi" fontSize="normal">
                {translate(
                  `owner_info.${selectedBusinessType}.upload_kitas.label`
                )}
              </Text>
              <Text marginBottomS fontSize="medium">
                {translate(
                  `owner_info.${selectedBusinessType}.upload_kitas.sublabel`
                )}
              </Text>
              <FilePickerWithPreview
                maxSize={10}
                onChange={(e) => _handleOnUpload(e, "kitas")}
                previewUrl={ownerInfoState.fileUpload.kitas.url}
                mediaType={ownerInfoState.fileUpload.kitas.mediaType}
                metadata={ownerInfoState.fileUpload.kitas.metadata}
                isLoading={ownerInfoState?.loadingState.has.uploadFileKitas}
                tempFile={ownerInfoState.fileUpload.kitas.temp}
                onClick={() =>
                  eventDispatcher.publish({ action: "ob_upload_kitas" })
                }
                onDelete={() => {
                  ownerInfoState.deleteFileUpload("kitas");
                  ownerInfoState.errorFields.kitas_upload.error = undefined;
                }}
                onError={(error) =>
                  eventDispatcher.publish({
                    action: "ob_upload_kitas_error",
                    payload: {
                      message: error.message,
                    },
                  })
                }
                isError={ownerInfoState.errorFields.kitas_upload.error}
              />
            </Box>

            <Box>
              <Input
                label={translate(
                  `owner_info.${selectedBusinessType}.name_on_kitas.label`
                )}
                placeholder={translate(
                  `owner_info.${selectedBusinessType}.name_on_kitas.placeholder`
                )}
                onChange={(e) =>
                  ownerInfoState.handleOnFieldChange("id_name", e.target.value)
                }
                onBlur={() => {
                  ownerInfoState.validateForm("id_name");
                }}
                error={errorFields.id_name.error}
                helperText={errorFields.id_name.helperText}
                value={form.id_name}
                maxLength={50}
                pattern="^[a-z A-Z .]*$"
              />
            </Box>
          </BoxColumnSectionEl>
        )}

        {selectedCitizenship === "WNI" &&
          selectedPaymentMethod === "card" &&
          selectedBusinessType === "individual" && (
            <BoxColumnSectionEl>
              <Box marginBottomL>
                <Text marginBottomS fontWeight="demi" fontSize="normal">
                  {translate(
                    `owner_info.${selectedBusinessType}.upload_npwp.label`
                  )}
                </Text>
                <Text marginBottomS fontSize="medium">
                  {translate(
                    `owner_info.${selectedBusinessType}.upload_npwp.sublabel`
                  )}
                </Text>
                <FilePickerWithPreview
                  maxSize={10}
                  onChange={(e) => _handleOnUpload(e, "npwp")}
                  previewUrl={ownerInfoState.fileUpload.npwp.url}
                  mediaType={ownerInfoState.fileUpload.npwp.mediaType}
                  metadata={ownerInfoState.fileUpload.npwp.metadata}
                  isLoading={ownerInfoState?.loadingState.has.uploadFileNpwp}
                  tempFile={ownerInfoState.fileUpload.npwp.temp}
                  onClick={() =>
                    eventDispatcher.publish({ action: "ob_upload_npwp" })
                  }
                  onDelete={() => {
                    ownerInfoState.deleteFileUpload("npwp");
                    ownerInfoState.errorFields.npwp_upload.error = undefined;
                  }}
                  onError={(error) =>
                    eventDispatcher.publish({
                      action: "ob_upload_npwp_error",
                      payload: {
                        message: error.message,
                      },
                    })
                  }
                  isError={ownerInfoState.errorFields.npwp_upload.error}
                />
              </Box>

              <Box>
                <Box>
                  <Input
                    label={translate(
                      `owner_info.${selectedBusinessType}.name_on_npwp.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.name_on_npwp.placeholder`
                    )}
                    onChange={(e) =>
                      ownerInfoState.handleOnFieldChange(
                        "npwp_name",
                        e.target.value
                      )
                    }
                    onBlur={() => {
                      ownerInfoState.validateForm("npwp_name");
                    }}
                    error={errorFields.npwp_name.error}
                    helperText={errorFields.npwp_name.helperText}
                    defaultValue={outlet.npwpName}
                    maxLength={50}
                  />
                </Box>
                <Box>
                  <Input
                    label={translate(
                      `owner_info.${selectedBusinessType}.npwp_number.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.npwp_number.placeholder`
                    )}
                    onChange={({ target }) => {
                      let { value } = target;
                      ownerInfoState.handleOnFieldChange("npwp", value);
                    }}
                    onBlur={() => ownerInfoState.validateForm("npwp", "npwp")}
                    error={errorFields.npwp.error}
                    helperText={errorFields.npwp.helperText}
                    value={form.npwp}
                    pattern="^[0-9]*$"
                    maxLength={16}
                    inputMode="numeric"
                  />
                </Box>
                <Box marginBottomL>
                  <TextArea
                    disabledResize
                    label={translate(
                      `owner_info.${selectedBusinessType}.address_on_npwp.label`
                    )}
                    placeholder={translate(
                      `owner_info.${selectedBusinessType}.address_on_npwp.placeholder`
                    )}
                    onChange={(e) =>
                      ownerInfoState.handleOnFieldChange(
                        "headquarters_address",
                        e.target.value
                      )
                    }
                    onBlur={() =>
                      ownerInfoState.validateForm("headquarters_address")
                    }
                    error={errorFields.headquarters_address.error}
                    helperText={errorFields.headquarters_address.helperText}
                    defaultValue={outlet.headquartersAddress}
                    maxLength={255}
                  />
                </Box>
              </Box>
            </BoxColumnSectionEl>
          )}

        {selectedCitizenship === "WNA" && (
          <BoxColumnSectionEl>
            <Box marginBottomL>
              <Text marginBottomS fontWeight="demi" fontSize="normal">
                {translate(
                  `owner_info.${selectedBusinessType}.upload_passport.label`
                )}
              </Text>
              <Text marginBottomS fontSize="medium">
                {translate(
                  `owner_info.${selectedBusinessType}.upload_passport.sublabel`
                )}
              </Text>
              <FilePickerWithPreview
                maxSize={10}
                onChange={(e) => _handleOnUpload(e, "passport")}
                previewUrl={ownerInfoState.fileUpload.passport.url}
                mediaType={ownerInfoState.fileUpload.passport.mediaType}
                metadata={ownerInfoState.fileUpload.passport.metadata}
                isLoading={ownerInfoState.loadingState.has.uploadFilePassport}
                tempFile={ownerInfoState.fileUpload.passport.temp}
                onClick={() =>
                  eventDispatcher.publish({ action: "ob_upload_passport" })
                }
                onDelete={() => {
                  ownerInfoState.deleteFileUpload("passport");
                  ownerInfoState.errorFields.passport_upload.error = undefined;
                }}
                onError={(error) =>
                  eventDispatcher.publish({
                    action: "ob_upload_passport_error",
                    payload: {
                      message: error.message,
                    },
                  })
                }
                isError={ownerInfoState.errorFields.passport_upload.error}
              />
            </Box>
          </BoxColumnSectionEl>
        )}

        <BoxButtonCtaEl flex justifyBetween marginTopXxl>
          <Link to={PASSPORT_ROUTES.paymentMethods}>
            <Button
              outline
              style={{ minWidth: "144px" }}
              onClick={() => {
                ownerInfoState.resetSelectedCitizenship();
                eventDispatcher.publish({ action: "ob_owner_back" });
              }}
            >
              {translate("owner_info.footer.cta_back")}
            </Button>
          </Link>
          <Button
            disabled={isFieldNotValid}
            loading={passportStore?.loadingState?.has?.updateOutlet!}
            type="submit"
            style={{ minWidth: "144px" }}
          >
            {translate("owner_info.footer.cta_continue")}
          </Button>
        </BoxButtonCtaEl>
      </Box>

      <LoadingOverlay show={passportStore?.loadingState?.has?.updateOutlet!} />
      <LegacyOwnerInfoModal
        show={showModalKTP}
        onClose={() => setShowModalKTP(false)}
        selectedBusinessType={selectedBusinessType}
      />
    </>
  );
});
